import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { QUERY_KEYS } from 'components/RabbitOKRSidePanel/datahooks';
import { closeSecondaryPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getMetrics } from 'services/goalTracking';
import { getAuthDetails } from 'services/plugins';

export const usePluginAuthorizationDetailQuery = (thirdPartyType) =>
  useQuery(
    [REACT_QUERY_KEYS.thirdPartyPlugin, 'authorization', thirdPartyType],
    () => getAuthDetails({ thirdPartyType }),
    { staleTime: Infinity, keepPreviousData: true, refetchOnWindowFocus: false, retry: 2 }
  );

export const usePluginTrackingMetricsQuery = ({ okrId, pluginType }) =>
  useQuery([REACT_QUERY_KEYS.thirdPartyPlugin, 'metrics', okrId, pluginType], () =>
    getMetrics({
      taskId: okrId,
      pluginType,
    })
  );

const linkMetricApi = ({
  taskId,
  metricId,
  pluginType,
  filters,
  name,
  isCustomQuery,
  query,
  queryType,
  ratioQuery,
}) => {
  const url = endpoints.goalTracking.linkV1;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      taskId,
      metricId,
      pluginType,
      filters,
      name,
      isCustomQuery,
      query,
      queryType,
      ratioQuery,
    },
  });
};
export const useLinkPluginMetricMutation = ({ okrId, pluginType }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  // const { onUpdate } = useOKRDrawerControlCallbacks();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const toast = useToast();

  const metricQueryKey = [REACT_QUERY_KEYS.thirdPartyPlugin, 'metrics', okrId, pluginType];
  const queryKey = [REACT_QUERY_KEYS.thirdPartyPlugin, 'link', okrId, pluginType];

  return useMutation({
    mutationFn: ({ metricId, filters, name, isCustomQuery, query, ratioQuery, queryType }) =>
      linkMetricApi({
        taskId: okrId,
        pluginType,
        metricId,
        filters,
        name,
        isCustomQuery,
        query,
        ratioQuery,
        queryType,
      }),
    onMutate: () => {
      queryClient.cancelQueries(queryKey);
    },
    onError: (err, newKR, context) => {
      toast({
        title:
          err.message ||
          `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} tracking metric could not be updated`,
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.KEY_RESULT, okrId]);
      queryClient.invalidateQueries(metricQueryKey);

      toast({
        title: `Your changes have been saved`,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });

      dispatch(closeSecondaryPanel());
    },
  });
};

const unlinkMetricApi = ({ taskId }) => {
  const url = endpoints.goalTracking.unlinkV1;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: {
      taskId,
    },
  });
};
export const useUnlinkPluginMetricMutation = ({ okrId }) => {
  const queryClient = useQueryClient();

  // const { onUpdate } = useOKRDrawerControlCallbacks();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const toast = useToast();

  const metricQueryKey = [REACT_QUERY_KEYS.thirdPartyPlugin, 'metrics', okrId];
  const queryKey = [REACT_QUERY_KEYS.thirdPartyPlugin, 'unlink', okrId];

  return useMutation({
    mutationFn: () =>
      unlinkMetricApi({
        taskId: okrId,
      }),
    onMutate: () => {
      queryClient.cancelQueries(queryKey);
    },
    onError: (err, newKR, context) => {
      toast({
        title:
          err.message ||
          `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} tracking metric could not be updated`,
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.KEY_RESULT, okrId]);
      queryClient.invalidateQueries(metricQueryKey);

      toast({
        title: `Your changes have been saved`,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
    },
  });
};

const refreshMetricApi = ({ okrId }) => {
  const url = endpoints.rabbitGoals.refreshMetric(okrId);
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
  });
};
export const useRefreshMetricMutation = ({ okrId }) => {
  const queryClient = useQueryClient();

  // const { onUpdate } = useOKRDrawerControlCallbacks();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const toast = useToast();

  const metricQueryKey = [REACT_QUERY_KEYS.thirdPartyPlugin, 'metrics', okrId];
  const queryKey = [REACT_QUERY_KEYS.thirdPartyPlugin, 'unlink', okrId];

  return useMutation({
    mutationFn: () =>
      refreshMetricApi({
        okrId,
      }),
    onMutate: () => {
      queryClient.cancelQueries(queryKey);
    },
    onError: (err, newKR, context) => {
      toast({
        title:
          err.message ||
          `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} tracking metric could not be updated`,
        status: TOAST_STATUSES.ERROR,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.KEY_RESULT, okrId]);
      queryClient.invalidateQueries(metricQueryKey);

      toast({
        title: `Your changes have been saved`,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
    },
  });
};
